import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

interface FeatureItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  sort_order: number;
}

interface FeaturesEditorProps {
  data: FeatureItem[];
  onSave: (data: FeatureItem[]) => void;
}

const ICON_OPTIONS = [
  { label: '计算器', value: 'CalculatorOutlined' },
  { label: '机器人/AI', value: 'RobotOutlined' },
  { label: '折线图', value: 'LineChartOutlined' },
  { label: '药盒', value: 'MedicineBoxOutlined' },
  { label: '实验/科研', value: 'ExperimentOutlined' },
  { label: '团队', value: 'TeamOutlined' },
  { label: '安全认证', value: 'SafetyCertificateOutlined' },
  { label: '闪电', value: 'ThunderboltOutlined' },
];

const FeaturesEditor: React.FC<FeaturesEditorProps> = ({ data, onSave }) => {
  const [items, setItems] = useState<FeatureItem[]>(data || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const doSave = (newItems: FeatureItem[]) => {
    setItems(newItems);
    onSave(newItems);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    doSave(newItems.map((item, i) => ({ ...item, sort_order: i + 1 })));
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a: FeatureItem, b: FeatureItem) => a.sort_order - b.sort_order,
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div dangerouslySetInnerHTML={{ __html: text.substring(0, 100) + (text.length > 100 ? '...' : '') }} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_: any, record: FeatureItem, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
            onClick={() => handleMove(index, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === items.length - 1}
            onClick={() => handleMove(index, 'down')} />
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(index)}>编辑</Button>
          <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(index)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    const item = items[index];
    form.setFieldsValue({
      id: item.id,
      title: item.title,
      description: item.description,
      icon: item.icon,
      sort_order: item.sort_order,
    });
    setModalVisible(true);
  };

  const handleDelete = (index: number) => {
    doSave(items.filter((_, i) => i !== index));
    message.success('删除成功');
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const newItem: FeatureItem = {
        id: values.id || `feature-${Date.now()}`,
        title: values.title,
        description: values.description,
        icon: values.icon,
        sort_order: values.sort_order,
      };

      let newItems: FeatureItem[];
      if (editingIndex !== null) {
        newItems = [...items];
        newItems[editingIndex] = newItem;
      } else {
        newItems = [...items, newItem];
      }
      doSave(newItems);
      setModalVisible(false);
      message.success(editingIndex !== null ? '更新成功' : '添加成功');
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加功能特性
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        pagination={false}
      />

      <Modal
        title={editingIndex !== null ? '编辑功能特性' : '添加功能特性'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={900}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="ID"
            name="id"
            rules={[{ required: editingIndex !== null, message: '请输入 ID' }]}
          >
            <Input placeholder="自动生成" disabled={editingIndex === null} />
          </Form.Item>

          <Form.Item
            label="图标"
            name="icon"
            rules={[{ required: true, message: '请选择图标' }]}
          >
            <Select placeholder="请选择图标" options={ICON_OPTIONS} />
          </Form.Item>

          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="例如：精准剂量计算" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea
              placeholder="支持 Markdown 格式&#10;例如：**粗体** *斜体* [链接](url)"
              rows={4}
            />
          </Form.Item>

          <Form.Item
            label="排序"
            name="sort_order"
            rules={[{ required: true, message: '请输入排序' }]}
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeaturesEditor;
